// @ts-check
import createUserPreference from './user-preference'
import pick from 'lodash.pick'
import displayPurposes from './display-purposes'
import { publisherRestrictionPurposeIds } from '../cmp/publisherRestrictions'

/**
 * @param {IntMap<Vendor>} vendors
 * @param {(...any) => void} log 
 * @returns {IntMap<Vendor>}
 */
function removeUnconsentableVendors (vendors, log) {
  const consentableVendors = Object.values(vendors).filter(vendor => {
    const consentable = vendor.purposes.length !== 0 || vendor.legIntPurposes.length !== 0 || vendor.specialPurposes.length !== 0
    !consentable && log('removing', vendor.id, vendor.name)
    return consentable
  })
  return consentableVendors.reduce((acc, vendor) => {
    acc[vendor.id] = vendor
    return acc
  }, {})
}

/**
 * @param {IntMap<Vendor>} vendors
 * @returns {IntMap<Vendor>}
 */
function applyFlexiblePurposes (vendors) {
  const restrictPurposeIds = new Set([...publisherRestrictionPurposeIds])
  return Object.entries(vendors).reduce((acc, [id, v]) => {
    v.purposes = Array.from(new Set([...v.purposes, ...v.flexiblePurposes.filter(purposeId => restrictPurposeIds.has(purposeId))]))
    v.legIntPurposes = v.legIntPurposes.filter(purposeId => !restrictPurposeIds.has(purposeId))
    acc[id] = v
    return acc
  }, {})
}

/**
 * @param {UIModelOpts} opts 
 * @returns {UIModel}
 */
export function createModel ({ cmpModel, currentLevel, log, uiLevel }) {
  const gvl = cmpModel.tcModel.gvl
  const { purposes, specialPurposes, features, specialFeatures } = gvl
  const stacks = pick(gvl.stacks, [42])
  const vendors = applyFlexiblePurposes(removeUnconsentableVendors(JSON.parse(JSON.stringify(gvl.vendors)), log))
  const userPrefs = createUserPreference(cmpModel)
  const nonTcfVendors = cmpModel.acmVendors.cvl
  const nonRegisteredVendors = cmpModel.nonRegisteredVendors.cvl
  return {
    config: {
      hash: {
        purposes,
        specialPurposes,
        features,
        specialFeatures,
        vendors,
        stacks,
        nonTcfVendors,
        nonRegisteredVendors
      },
      userPrefs,
      displayPurposes
    },
    currentLevel: currentLevel || uiLevel
  }
}

/**
 * @typedef {Object} UIModelOpts
 * @property {import('./Ui').UiLevel} currentLevel
 * @property {import('./Ui').UiLevel} uiLevel
 * @property {import('./Ui').CmpModelForUi} cmpModel
 * @property {(...any) => void} log
 */

/**
 * @typedef {Object} UIModel
 * @property {UIModelConfig} config
 * @property {import('./Ui').UiLevel} currentLevel
 */

/**
 * @typedef {Object} UIModelConfig
 * @property {UIGVLModel} hash
 * @property {import('./user-preference').UserPrefs} userPrefs
 * @property {DisplayPurposes} displayPurposes
 */
/**
 * @typedef {Object} UIGVLModel
 * @property {IntMap<Purpose>} purposes
 * @property {IntMap<Purpose>} specialPurposes
 * @property {IntMap<Feature>} features
 * @property {IntMap<Purpose>} specialFeatures
 * @property {IntMap<Vendor>} vendors
 * @property {IntMap<Stack>} stacks
 * @property {import('../cmp/NonIABModel').NonIABVendorList} nonTcfVendors
 * @property {import('../cmp/NonIABModel').NonIABVendorList} nonRegisteredVendors
 */

/**
 * @template T
 * @typedef {import('@mol-ads/tcf-core').IntMap<T>} IntMap
 */

/**
 * @typedef {import('@mol-ads/tcf-core').Purpose} Purpose
 */

/**
 * @typedef {import('@mol-ads/tcf-core').Feature} Feature
 */

 /**
  * @typedef {import('@mol-ads/tcf-core').Vendor} Vendor
  */

 /**
  * @typedef {import('@mol-ads/tcf-core').Stack} Stack
  */

 /**
  * @typedef {Array<{type: string, id: number}>} DisplayPurposes
  */